/**
 * Frontend error reporter — captures unhandled errors and sends to shared-api.
 */

const ERROR_API_URL = "https://api.g1.network/errors";
const SOURCE = "admin_console";
const MAX_ERRORS_PER_MINUTE = 10;
const BATCH_DELAY_MS = 2000;

let errorCount = 0;
let resetTimer: ReturnType<typeof setTimeout> | null = null;
const batch: Array<Record<string, unknown>> = [];
let batchTimer: ReturnType<typeof setTimeout> | null = null;

function resetCounter() {
	errorCount = 0;
	resetTimer = null;
}

function flushBatch() {
	const items = batch.splice(0);
	batchTimer = null;
	for (const item of items) {
		fetch(ERROR_API_URL, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(item),
		}).catch(() => {}); // fire and forget
	}
}

function queueError(data: Record<string, unknown>) {
	errorCount++;
	if (!resetTimer) resetTimer = setTimeout(resetCounter, 60000);
	if (errorCount > MAX_ERRORS_PER_MINUTE) return;

	batch.push(data);
	if (!batchTimer) batchTimer = setTimeout(flushBatch, BATCH_DELAY_MS);
}

export function initErrorReporter() {
	// Uncaught errors
	window.addEventListener("error", (event) => {
		queueError({
			source: SOURCE,
			level: "error",
			message: event.message || "Unknown error",
			stack: event.error?.stack ?? null,
			path: window.location.pathname,
			metadata: {
				type: "uncaught",
				filename: event.filename,
				lineno: event.lineno,
				colno: event.colno,
			},
		});
	});

	// Unhandled promise rejections
	window.addEventListener("unhandledrejection", (event) => {
		const message = event.reason instanceof Error ? event.reason.message : String(event.reason);
		const stack = event.reason instanceof Error ? event.reason.stack : null;
		queueError({
			source: SOURCE,
			level: "error",
			message,
			stack,
			path: window.location.pathname,
			metadata: { type: "unhandled_rejection" },
		});
	});

	// Intercept fetch errors (4xx/5xx API responses)
	const originalFetch = window.fetch;
	window.fetch = async (...args) => {
		const res = await originalFetch(...args);
		if (res.status >= 400) {
			const url =
				typeof args[0] === "string" ? args[0] : args[0] instanceof Request ? args[0].url : "";
			// Don't report errors from the error API itself
			if (!url.includes("/errors")) {
				const method = (args[1]?.method ?? "GET").toUpperCase();
				let errorMessage = `HTTP ${res.status}`;
				try {
					const body = await res.clone().json();
					if (body.error) errorMessage = body.error;
				} catch {}

				queueError({
					source: SOURCE,
					level: res.status >= 500 ? "error" : "warn",
					statusCode: res.status,
					method,
					path: new URL(url).pathname,
					message: errorMessage,
					metadata: { type: "api_error", fullUrl: url },
				});
			}
		}
		return res;
	};
}
